import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import assistantImageUpload from '../../assets/icons/assistantImageUpload.svg';
import magicWand from '../../assets/images/aha-animated-wand.svg';
import { TOOLTIP_DELAY_GENERAL } from '../../constants';
import AssistantController from '../../controllers/AssistantController';
import { ALLOWED_IMAGE_TYPES } from '../../shapes/ShapeImage';
import styles from '../../styles.module.css';
import WithHint from '../../toolbars/WithHint';
import {
  ToolAssistantGo,
  ToolAssistantStop,
} from '../../tools/ToolAssistantGo';
import customElementBoolAttr from '../../utils/customElementBoolAttr';
import { svgToIcon, svgToURL } from '../../utils/svg';
import assistantStyles from './Assistant.module.css';

export const SUMMARIZE_PROMPT_START =
  'Analyze this whiteboard to identify themes, action items, and decisions';

const SAMPLE_PROMPTS = {
  'Arrange by theme':
    'Identify common themes in this whiteboard (for example: area of the product, workflow phase, or feedback source). Select the best theme for each shape, move shapes to create theme clusters, and add labels to each theme.',
  'Summarize whiteboard': `${SUMMARIZE_PROMPT_START}. Write an executive summary of two to four sentences and summarize four to six key points. Add the summary in a rectangle shape with a left-aligned bullet point list.`,
  'Generate a wireframe':
    'Create a wireframe for a fitness tracker called Fredwin Cycling. This web-based product should include application navigation on the left side, a menu bar, and a homepage with a title, welcome message, leaderboard, graph of miles ridden, and button to log new rides.',
};

const trackEvent = (action, properties) => {
  if (window.AhaAnalytics) {
    window.AhaAnalytics.track(
      'Feature-7408587598318838983',
      action,
      properties
    );
  }
};

const Menu: React.FC<{
  history: string[];
  setPromptValue: (value: string) => void;
  previousHistoryItem: () => void;
  nextHistoryItem: () => void;
  openImagePicker: () => void;
  currentHistoryItemNumber: number;
  waitingForResponse: boolean;
}> = ({
  history,
  setPromptValue,
  previousHistoryItem,
  nextHistoryItem,
  openImagePicker,
  currentHistoryItemNumber,
  waitingForResponse,
}) => {
  return (
    <aha-menu>
      <aha-button kind='icon' slot='control'>
        <aha-icon icon='fa-regular fa-ellipsis-h' />
      </aha-button>
      <aha-menu-content>
        <aha-menu-group>
          {Object.keys(SAMPLE_PROMPTS).map(searchTerm => (
            <aha-menu-item
              key={searchTerm}
              disabled={customElementBoolAttr(waitingForResponse)}
            >
              <a
                onClick={() => {
                  trackEvent('User selects whiteboard AI example prompt', {
                    'Example prompt': searchTerm,
                  });
                  setPromptValue(SAMPLE_PROMPTS[searchTerm]);
                }}
              >
                <aha-ai-icon />
                {searchTerm}
              </a>
            </aha-menu-item>
          ))}
        </aha-menu-group>
        <aha-menu-group>
          <div className={assistantStyles.assistantMenuBottomBar}>
            <div>
              <WithHint
                name='Generate wireframe from image'
                delay={TOOLTIP_DELAY_GENERAL}
                appendTo='parent'
              >
                <div
                  onClick={() => {
                    !waitingForResponse && openImagePicker();
                  }}
                  className={classNames(
                    styles.toolButton,
                    assistantStyles.assistantImageUploadButton,
                    { [styles.toolButtonDisabled]: waitingForResponse }
                  )}
                >
                  {svgToIcon(assistantImageUpload, { width: 18 })}
                </div>
              </WithHint>
            </div>
            <div className={assistantStyles.assistantBackForward}>
              <aha-button
                disabled={customElementBoolAttr(
                  currentHistoryItemNumber === 0 || waitingForResponse
                )}
                size='small'
                kind='plain'
                onClick={previousHistoryItem}
              >
                <i className='fa-regular fa-chevron-left'></i>
              </aha-button>
              <aha-button
                disabled={customElementBoolAttr(
                  currentHistoryItemNumber >= history.length ||
                    waitingForResponse
                )}
                size='small'
                kind='plain'
                onClick={nextHistoryItem}
              >
                <i className='fa-regular fa-chevron-right'></i>
              </aha-button>
            </div>
          </div>
        </aha-menu-group>
      </aha-menu-content>
    </aha-menu>
  );
};

const AssistantToolbar: React.FC = () => {
  const controller = useController(AssistantController);
  const { waitingForResponse } = controller.state;
  const whiteboardController = controller.whiteboardController;

  const history = controller.promptHistory();

  const inputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [promptValue, setPromptValue] = useState('');
  const [lastPrompt, setLastPrompt] = useState('');
  const [currentHistoryItemNumber, setCurrentHistoryItemNumber] = useState(
    history.length
  );

  const nextHistoryItem = () => {
    if (currentHistoryItemNumber < history.length) {
      setCurrentHistoryItemNumber(currentHistoryItemNumber + 1);
      if (currentHistoryItemNumber + 1 === history.length) {
        setPromptValue('');
      } else {
        setPromptValue(history[currentHistoryItemNumber + 1]);
      }
    }
  };

  const previousHistoryItem = () => {
    if (currentHistoryItemNumber > 0) {
      setCurrentHistoryItemNumber(currentHistoryItemNumber - 1);
      setPromptValue(history[currentHistoryItemNumber - 1]);
    }
  };

  const runPrompt = promptString => {
    if (waitingForResponse) {
      whiteboardController.actionApplyTool(ToolAssistantStop, {
        whiteboardController,
        assistantController: controller,
      });
      return;
    }

    if (promptString.length === 0) {
      return;
    }

    setLastPrompt(promptString);

    whiteboardController.actionApplyTool(ToolAssistantGo, {
      whiteboardController,
      assistantController: controller,
      selection: whiteboardController.toolbarObject(),
      prompt: promptString,
    });

    trackEvent('User submits whiteboard AI prompt', {});

    setPromptValue('');
    // Set it to +1 which is presently beyond the bounds of the history, but
    // will immediately be correct once the controller action updates it.
    setCurrentHistoryItemNumber(history.length + 1);
  };

  const openImagePicker = () => {
    if (imageInputRef.current) {
      const imageInput: HTMLInputElement = imageInputRef.current;
      imageInput.click();
    }
  };

  const onFileSelected = event => {
    const imageFile = event.target.files[0];
    if (imageFile) {
      whiteboardController.actionApplyTool(ToolAssistantGo, {
        whiteboardController,
        assistantController: controller,
        selection: whiteboardController.toolbarObject(),
        image: imageFile,
      });

      trackEvent('User generates wireframe from image', {});
      // Clear the file input so that the same file can be selected again
      event.target.value = '';
    }
  };

  const handleKey = event => {
    switch (event.key) {
      case 'Enter':
        runPrompt(promptValue);
        event.preventDefault();
        break;

      case 'Escape':
        event.preventDefault();
        whiteboardController.actionToggleAIAssistant(false);
        break;

      case 'ArrowUp':
        event.preventDefault();
        previousHistoryItem();
        break;

      case 'ArrowDown':
        event.preventDefault();
        nextHistoryItem();
        break;
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      const input: HTMLInputElement = inputRef.current;
      input.focus();
      input.select();
    }
  }, []);

  return (
    <>
      <div className={styles.assistantToolbarContainer}>
        <div
          className={classNames(styles.toolbarCommon, styles.assistantToolbar)}
          key={'assistant-toolbar'}
        >
          {waitingForResponse ? (
            <img src={svgToURL(magicWand)} />
          ) : (
            <i
              className={classNames(
                'fa-solid fa-wand-magic-sparkles fa-fw',
                assistantStyles.assistantWandIcon
              )}
            />
          )}
          <div
            className={assistantStyles.assistantPromptGrow}
            data-replicated-value={promptValue}
          >
            <textarea
              rows={1}
              ref={inputRef}
              className={assistantStyles.assistantPrompt}
              placeholder='Tell AI what to create or change'
              value={waitingForResponse ? lastPrompt : promptValue}
              onChange={event => setPromptValue(event.target.value)}
              onKeyDown={event => handleKey(event)}
              disabled={waitingForResponse}
            />
          </div>
          <Menu
            history={history}
            setPromptValue={setPromptValue}
            previousHistoryItem={previousHistoryItem}
            nextHistoryItem={nextHistoryItem}
            currentHistoryItemNumber={currentHistoryItemNumber}
            openImagePicker={openImagePicker}
            waitingForResponse={waitingForResponse}
          />
          <aha-button
            kind='primary'
            disabled={customElementBoolAttr(
              promptValue.length === 0 && !waitingForResponse
            )}
            onClick={() => runPrompt(promptValue)}
          >
            {waitingForResponse && <i className='fa-solid fa-stop'></i>}
            {!waitingForResponse && 'Go'}
          </aha-button>

          <input
            style={{ display: 'none' }}
            onChangeCapture={onFileSelected}
            accept={ALLOWED_IMAGE_TYPES.join(',')}
            type='file'
            ref={imageInputRef}
          />

          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                height: '32px',
                borderLeft: '1px solid var(--theme-primary-border)',
                paddingRight: 'var(--spacer-1)',
                marginLeft: 'var(--spacer-1)',
              }}
            ></span>

            <aha-button
              kind='icon'
              onClick={() =>
                whiteboardController.actionToggleAIAssistant(false)
              }
            >
              <i className='fa-solid fa-xmark'></i>
            </aha-button>
          </span>
        </div>
      </div>
    </>
  );
};

export default StartControllerScope(
  AssistantController,
  ApplicationView(AssistantToolbar)
);
